import ReactGA from 'react-ga4'

import { CardListType, CardType, PageCardType } from '@/types/card'
import { formatUrl } from '@/utils/helpers'

import requestService from './request.service'

let cardCache: Record<string, CardType> = {}
const slugify = (nameOrSlug: string) => formatUrl(nameOrSlug.split(' // ')[0].trim())

// Keep a rolling 60s window of card fetches and send one GA summary per minute.
const GA_REPORT_INTERVAL = 60_000
const CARD_REQUEST_MAX_RETRIES = 4
const CARD_REQUEST_RETRY_DELAY = 1000
const cardApiRequestTimestamps: number[] = []
let lastCardApiSummarySentAt = 0
const reportGetCard = () => {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') return

  const now = Date.now()

  // Drop timestamps older than 60s
  const cutoff = now - GA_REPORT_INTERVAL
  let dropCount = 0
  while (dropCount < cardApiRequestTimestamps.length && cardApiRequestTimestamps[dropCount] <= cutoff) dropCount++
  if (dropCount) cardApiRequestTimestamps.splice(0, dropCount)

  // Track this request
  cardApiRequestTimestamps.push(now)

  // Return if it's not yet time to send an event
  if (now - lastCardApiSummarySentAt < GA_REPORT_INTERVAL) return

  // Send event and update timestamp
  const labelBucketSize = 10
  const label = Math.floor(cardApiRequestTimestamps.length / labelBucketSize) * labelBucketSize
  ReactGA.event('card_gets_last_minute', { label, nonInteraction: true })
  console.log(`card_gets_last_minute: ${cardApiRequestTimestamps.length}`)
  lastCardApiSummarySentAt = now
}

const getCard = async (
  nameOrSlug: string,
  { collectorNumber, setCode }: { collectorNumber?: string; setCode?: string } = {},
): Promise<CardType> => {
  const slug = slugify(nameOrSlug)
  if (!slug) throw new Error('Invalid card name')

  // Build query string and cache key
  const params = new URLSearchParams()
  if (setCode) {
    params.set('set', setCode)
    if (collectorNumber) params.set('collector_number', collectorNumber)
  }
  const query = params.toString()
  const key = query ? `${slug}?${query}` : slug

  if (cardCache[key]) return cardCache[key]

  const url = setCode
    ? `${process.env.NEXT_PUBLIC_EDHREC_API_URL_CLOUDFLARE}/card/${key}`
    : `${process.env.NEXT_PUBLIC_EDHREC_JSON_URL_CLOUDFLARE}/cards/${slug}`
  reportGetCard()
  let res = await requestService.get(url, {}, true)
  for (let attempt = 0; attempt < CARD_REQUEST_MAX_RETRIES && res.status >= 500 && res.status < 600; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, CARD_REQUEST_RETRY_DELAY))
    res = await requestService.get(url, {}, true)
  }
  const card = await res.json()

  // Update cache
  cardCache[key] = card

  return card
}

const getCardMap = async (names: string[], abortable = true): Promise<Record<string, CardType>> => {
  let cards: Record<string, CardType> = {}
  const remainingCardNames: string[] = []

  for (const name of names) {
    if (!name) {
      const e = new Error()
      console.warn('Invalid card name:', name, e.stack)
      continue
    }
    if (cardCache[name]) cards[name] = cardCache[name]
    else remainingCardNames.push(name)
  }

  if (remainingCardNames) {
    const chunkSize = 100
    for (let i = 0; i < remainingCardNames.length; i += chunkSize) {
      const chunk = remainingCardNames.slice(i, i + chunkSize)
      const res = await requestService.post(
        '/api/cards/',
        {
          format: 'dict',
          names: chunk,
        },
        {},
        true,
        abortable,
      )
      cards = { ...cards, ...(await res.json()).cards }
    }
  }

  // Update cache
  cardCache = { ...cardCache, ...cards }

  return cards
}

const getPageCardMap = (
  cardMap: Record<string, CardType>,
  initCardLists: CardListType[],
): Record<string, PageCardType> => {
  // Get additional card data from cardlists
  const cards = initCardLists.reduce(
    (acc: PageCardType[], cardList: CardListType) => [
      ...acc,
      ...cardList.cardviews.flatMap((cardview) => {
        const cardFaces = cardview.cards || [{ name: cardview.name, url: cardview.sanitized }]
        return cardFaces.map(({ name, url }) => ({
          ...cardview,
          name,
          url,
        })) as PageCardType[]
      }),
    ],
    [],
  )
  const pageCardList = Object.keys(cardMap).map((key) => {
    const card = cardMap[key]
    const pageCard = cards.find((c) => c.url === card.url)
    return {
      ...card,
      ...pageCard,
    }
  }) as PageCardType[]

  // Add cardlist data to card map
  const pageCardMap = pageCardList.reduce(
    (acc, card) => {
      acc[card.url as string] = card
      return acc
    },
    {} as Record<string, PageCardType>,
  )

  return pageCardMap
}

const updateCardCache = (newCache: Record<string, CardType>) => {
  cardCache = { ...cardCache, ...newCache }
}

const cardService = { getCard, getCardMap, getPageCardMap, updateCardCache }

export default cardService
